import {
  Button,
  Checkbox,
  DatePicker,
  Divider,
  Input,
  message,
  Modal,
  Select,
  Spin,
} from "antd";
import { useLanguageTranslator } from "hooks/use-language-translator";
import { UseApiCallV2, UseLazyApiCallV2 } from "hooks";
import { useParams } from "react-router-dom";
import { ConditionalRenderer } from "components";
import { dateKeys, dropdownKeys } from "pages/cases-detail/constants";
import dayjs from "dayjs";
import { CalenderIcon, DropdownSuffix } from "icons";
import { DocumentPreview } from "components/documentPreview";
import { useRecoilState } from "recoil";
import { caseDetailFormAtom } from "store/atom";
import styles from "./styles.module.scss";
import { useEffect, useState } from "react";
import { useCurrentUserContext } from "providers";

export const ExtractedDataModal = ({
  open,
  handleCloseModal,
  setQueries,
  queries,
  refetchDetail,
  documentType,
  fileName,
  allowUpdate = true,
  dropdownsListObj,
  extractedKeysForm,
  setExtractedKeysForm,
}) => {
  const { user } = useCurrentUserContext();
  const { translator } = useLanguageTranslator();
  const extractedTrans = translator("caseDetail.extractedData");
  const { caseId } = useParams();
  const [formData, setFormData] = useRecoilState(caseDetailFormAtom);
  const [dropdownsListObjAll, setDropdownsListObjAll] = useState();
  const [
    getCarModel,
    { data: carModelData, isLoading: carModelLoading, error: carModelError },
  ] = UseLazyApiCallV2({
    url: "/get_car_model",
    method: "POST",
  });

  const { data: fileUrl, isLoading } = UseApiCallV2({
    url: "//download_pdf",
    method: "POST",
    skip: !open,
    paramsData: {
      case_id: caseId,
      documentType,
      fileName,
    },
  });

  const [
    updateDataFromQuery,
    {
      data: updateDataFromQueryData,
      isLoading: updateDataFromQueryLoading,
      error: updateDataFromQueryError,
    },
  ] = UseLazyApiCallV2({
    url: "/update_query",
    method: "POST",
  });

  const [
    caseHistoryUpdate,
    {
      data: caseHistoryUpdateData,
      isLoading: caseHistoryUpdateLoading,
      error: caseHistoryUpdateError,
    },
  ] = UseLazyApiCallV2({
    url: "/case_history_update",
    method: "POST",
  });

  useEffect(() => {
    setDropdownsListObjAll({ ...dropdownsListObj });
  }, []);

  function handleChangeInput({
    value,
    i,
    parentKey = null,
    sectionIndex = null,
    childIndex = null,
    key,
  }) {
    setExtractedKeysForm((prev) => ({ ...prev, [key]: value })); //this state is for flat form object to use for api call payload mainly

    setQueries((prev) =>
      prev.map((q, qIndex) => {
        // 🎯 Case 1: Update child field inside a parent
        if (
          q.key === parentKey &&
          q.isParent &&
          sectionIndex !== null &&
          childIndex !== null
        ) {
          const updatedParent = { ...q };
          updatedParent.sections = updatedParent.sections.map(
            (section, sIdx) => {
              if (sIdx === sectionIndex) {
                return {
                  ...section,
                  childs: section.childs.map((child, cIdx) =>
                    cIdx === childIndex ? { ...child, Answer: value } : child,
                  ),
                };
              }
              return section;
            },
          );
          return updatedParent;
        }

        // 🎯 Case 2: Update non-parent field
        if (qIndex === i && !parentKey) {
          return { ...q, Answer: value };
        }

        return q;
      }),
    );
  }

  async function getCarModelOptions({ id }) {
    if (!id) return;
    try {
      const data = await getCarModel({
        params: {
          manufacturerid: id,
        },
      });
      const final = data?.carCompanies?.map((item) => ({
        label: item?.modelName,
        value: item?.id,
      }));
      setDropdownsListObjAll((prev) => ({ ...prev, carModel: [...final] }));
    } catch (error) {
      message.error(translator("somethingWentWrong"));
    }
  }

  useEffect(() => {
    if (extractedKeysForm?.carCompany) {
      const selectedObj = dropdownsListObj?.carCompany?.find(
        (item) => item?.label === extractedKeysForm?.carCompany,
      );
      if (!selectedObj) {
        setDropdownsListObjAll((prev) => ({ ...prev, carModel: [] }));
      }
      if (selectedObj?.value) {
        getCarModelOptions({ id: selectedObj?.value });
      }
    }
  }, [extractedKeysForm?.carCompany]);

  function updateRecentlyUpdateDataStatus(data) {
    const updatedKeys = Object.keys(data);

    const formKeysActive = formData?.map((item) => {
      if (updatedKeys.includes(item.key)) {
        return { ...item, active: true };
      } else {
        return { ...item };
      }
    });
    setFormData(formKeysActive);
  }

  const preparePayload = (queries, caseId) => {
    let data = {};

    queries.forEach((item) => {
      // ✅ Case 1: Parent with childs
      if (item.isParent && item.sections) {
        const collected = [];

        item.sections.forEach((section) => {
          section.childs.forEach((child) => {
            if (child.checked && child.Answer) {
              // Save child field separately
              data[child.Query] = child.Answer;

              // Collect for parent combination
              collected.push(child.Answer);
            }
          });
        });

        // Build combined value for parent
        if (collected.length > 0) {
          data[item.Query] = collected.join(", ");
        }
      }

      // ✅ Case 2: Normal (non-parent) field
      else if (item.checked && item.Answer) {
        data[item.Query] = item.Answer;
      }
    });

    return {
      case_id: caseId,
      query: data,
    };
  };

  async function handleUpdateQueryData(queries) {
    const onlySelectedQueries = queries.filter((item) => item.checked);
    let data = {};
    for (const element of onlySelectedQueries) {
      data[element.Query] = element.Answer;
    }
    const params = preparePayload(queries, caseId);

    const paramsForHistoryUpdate = {
      query: { ...params?.query },
      case_id: caseId,
      is_manual: false,
      key_updated: null,
      loggedUser: user?.email,
    };

    try {
      await caseHistoryUpdate({
        params: paramsForHistoryUpdate,
      });
    } catch (error) {
      console.log({ error });
    }
    try {
      const res = await updateDataFromQuery({
        params,
      });
      if (res) {
        await refetchDetail();
        message.success(translator("updatedSuccess"));
        updateRecentlyUpdateDataStatus(data);

        handleCloseModal();
      }
    } catch (error) {
      message.error(translator("somethingWentWrong"));
    }
  }

  function getFormateDate(date) {
    if (date) {
      if (date?.includes(".")) {
        const format = date?.replace(/\./g, "-");
        return dayjs(format, ["DD-MM-YYYY", "DD-MM-YY"], true);
      } else if (date?.includes("-")) {
        return dayjs(date);
      }
    }
  }
  function handleCheck(
    item,
    i,
    parentKey = null,
    sectionIndex = null,
    childIndex = null,
  ) {
    setQueries((prev) =>
      prev.map((q, qIndex) => {
        // 🎯 Handle Parent Check Toggle
        if (qIndex === i && !parentKey) {
          const updatedParent = { ...q, checked: !q.checked };

          if (q.isParent && q.sections) {
            updatedParent.sections = q.sections.map((section) => ({
              ...section,
              childs: section.childs.map((child) => ({
                ...child,
                checked: updatedParent.checked,
              })),
            }));
          }

          return updatedParent;
        }

        // 🎯 Handle Child Check Toggle
        if (
          q.key === parentKey &&
          q.isParent &&
          sectionIndex !== null &&
          childIndex !== null
        ) {
          const updatedParent = { ...q };
          updatedParent.sections = updatedParent.sections.map(
            (section, sIdx) => {
              if (sIdx === sectionIndex) {
                return {
                  ...section,
                  childs: section.childs.map((child, cIdx) =>
                    cIdx === childIndex
                      ? { ...child, checked: !child.checked }
                      : child,
                  ),
                };
              }
              return section;
            },
          );

          // update parent checked (true if at least one child checked)
          const anyChildChecked = updatedParent.sections.some((s) =>
            s.childs.some((c) => c.checked),
          );
          updatedParent.checked = anyChildChecked;

          return updatedParent;
        }

        return q;
      }),
    );
  }

  return (
    <Modal
      destroyOnClose
      // title="Set Reminder"
      open={open}
      onCancel={handleCloseModal}
      footer={null}
      centered
      width="100dvw"
      styles={{
        content: {
          minHeight: "97dvh",
        },
      }}
    >
      <Spin spinning={updateDataFromQueryLoading || isLoading}>
        <div className={styles.previewAndData}>
          <div className={styles.previewFile}>
            <DocumentPreview signedUrl={fileUrl?.download_url} />
          </div>

          <div className={styles.container}>
            <div className={styles.extractedFields}>
              <h3 style={{ marginBottom: "15px" }}>{extractedTrans.heading}</h3>
              {queries?.map((mainItem, i) => {
                return (
                  <>
                    <ConditionalRenderer condition={mainItem?.isParent}>
                      <Divider />
                      <ConditionalRenderer
                        condition={mainItem?.sections?.length > 0}
                      >
                        <div
                          style={{
                            display: "flex",
                            gap: "3px",
                            alignItems: "center",
                            margin: "10px 0",
                          }}
                        >
                          <div
                            className={styles.number}
                            style={{
                              pointerEvents: "none",
                              backgroundColor: "#CD2A3F",
                            }}
                          >
                            {mainItem?.id}
                          </div>
                          <h3>
                            {translator(`caseDetail.form.${mainItem.key}`)}
                          </h3>
                        </div>
                      </ConditionalRenderer>

                      {mainItem?.sections?.map((section, sIndex) => (
                        <>
                          <div>
                            <ConditionalRenderer
                              condition={section?.sectionLabel}
                            >
                              <h4>
                                {translator(
                                  `caseDetail.form.${section?.sectionLabel}`,
                                )}
                              </h4>
                            </ConditionalRenderer>
                            <ConditionalRenderer condition={section?.childs}>
                              {/* <h5>{section?.childs[0].key}</h5> */}
                              {section?.childs?.map((item, cIndex) => (
                                <>
                                  <div
                                    className={styles.queryOption}
                                    key={item.Query}
                                  >
                                    <div
                                      style={{
                                        display: "flex",
                                        gap: "10px",
                                        alignItems: "center",
                                      }}
                                    >
                                      <div style={{ width: "27px" }}>
                                        <div
                                          className={`${styles.number} ${
                                            !item?.checked
                                              ? styles.inactiveNumber
                                              : ""
                                          }`}
                                          onClick={() =>
                                            handleCheck(
                                              mainItem,
                                              i,
                                              mainItem?.key,
                                              sIndex,
                                              cIndex,
                                            )
                                          }
                                        >
                                          {item?.id}
                                        </div>
                                      </div>
                                      <div className={styles.label}>
                                        {translator(
                                          `caseDetail.form.${item?.label}`,
                                        )}
                                      </div>
                                    </div>
                                    <ConditionalRenderer
                                      condition={allowUpdate}
                                    >
                                      {(dateKeys.includes(item.Query) ===
                                        true ||
                                        item?.type === "date") && (
                                        <div
                                          className={styles.datePickerWrapper}
                                        >
                                          <DatePicker
                                            inputReadOnly
                                            className={styles.date}
                                            suffixIcon={<CalenderIcon />}
                                            value={
                                              item?.Answer
                                                ? getFormateDate(item.Answer)
                                                : null
                                            }
                                            onChange={(date, dateString) =>
                                              handleChangeInput({
                                                value: dateString,
                                                i,
                                                parentKey: mainItem?.key,
                                                sectionIndex: sIndex,
                                                childIndex: cIndex,
                                                key: item?.key,
                                              })
                                            }
                                            name={item.Query}
                                            // style={{ pointerEvents: "none" }}
                                          />
                                        </div>
                                      )}
                                      <ConditionalRenderer
                                        condition={
                                          item.type === "text" ||
                                          item.type === "number"
                                        }
                                      >
                                        <Input
                                          type={item.type}
                                          value={item?.Answer || ""}
                                          onChange={(e) =>
                                            handleChangeInput({
                                              value: e.target.value,
                                              i,
                                              parentKey: mainItem?.key,
                                              sectionIndex: sIndex,
                                              childIndex: cIndex,
                                              key: item?.key,
                                            })
                                          }
                                          // disabled={!item?.checked}
                                          className={styles.input}
                                          style={{
                                            pointerEvents:
                                              item?.value === "Deutschland"
                                                ? "none"
                                                : "auto",
                                          }}
                                        />
                                      </ConditionalRenderer>

                                      {item?.type === "dropdown" && (
                                        <div
                                          style={{
                                            position: "relative",
                                            width: "250px",
                                          }}
                                        >
                                          <Select
                                            showSearch
                                            allowClear
                                            filterOption={(
                                              input,
                                              option, // 👈 filter logic
                                            ) =>
                                              (option?.label ?? "")
                                                .toLowerCase()
                                                .includes(input.toLowerCase())
                                            }
                                            suffixIcon={<DropdownSuffix />}
                                            value={item.Answer}
                                            style={{
                                              width: "100%",
                                              height: "40px",
                                            }}
                                            placeholder=""
                                            onChange={(e, data) =>
                                              handleChangeInput({
                                                value: data?.label,
                                                i,
                                                parentKey: mainItem?.key,
                                                sectionIndex: sIndex,
                                                childIndex: cIndex,
                                                key: item?.key,
                                              })
                                            }
                                            options={
                                              dropdownsListObjAll?.[item.key]
                                            }
                                          />
                                        </div>
                                      )}
                                    </ConditionalRenderer>
                                    <ConditionalRenderer
                                      condition={!allowUpdate}
                                    >
                                      <Input
                                        value={item?.Answer || ""}
                                        className={styles.input}
                                        style={{
                                          pointerEvents: "none",
                                        }}
                                      />
                                    </ConditionalRenderer>
                                  </div>
                                </>
                              ))}
                            </ConditionalRenderer>
                          </div>
                        </>
                      ))}
                      <ConditionalRenderer
                        condition={mainItem?.sections?.length > 0}
                      >
                        <Divider />
                      </ConditionalRenderer>
                    </ConditionalRenderer>
                    <ConditionalRenderer condition={!mainItem?.isParent}>
                      <div className={styles.queryOption} key={mainItem.Query}>
                        <div
                          style={{
                            display: "flex",
                            gap: "10px",
                            alignItems: "center",
                          }}
                        >
                          <div style={{ width: "27px" }}>
                            <div
                              className={`${styles.number} ${
                                !mainItem?.checked ? styles.inactiveNumber : ""
                              }`}
                              onClick={() => handleCheck(mainItem, i)}
                            >
                              {mainItem?.id}
                            </div>
                          </div>
                          <div className={styles.label}>
                            {" "}
                            {translator(`caseDetail.form.${mainItem?.key}`)}
                          </div>
                        </div>
                        <ConditionalRenderer condition={allowUpdate}>
                          <ConditionalRenderer
                            condition={
                              mainItem?.type === "text" ||
                              mainItem?.type === "number"
                            }
                          >
                            <Input
                              type="text"
                              value={mainItem?.Answer || ""}
                              onChange={(e) =>
                                handleChangeInput({
                                  value: e.target.value,
                                  i,
                                  key: mainItem?.key,
                                })
                              }
                              // disabled={!item?.checked}
                              className={styles.input}
                              style={{
                                pointerEvents:
                                  mainItem?.value === "Deutschland" ||
                                  !allowUpdate
                                    ? "none"
                                    : "auto",
                              }}
                            />
                          </ConditionalRenderer>
                          {mainItem?.type === "date" && (
                            <div className={styles.datePickerWrapper}>
                              <DatePicker
                                inputReadOnly
                                className={styles.date}
                                suffixIcon={<CalenderIcon />}
                                value={
                                  mainItem?.Answer
                                    ? getFormateDate(mainItem.Answer)
                                    : null
                                }
                                onChange={(date, dateString) =>
                                  handleChangeInput({
                                    value: dateString,
                                    i,
                                    key: mainItem?.key,
                                  })
                                }
                                // name={mainItem.Query}
                                // style={{ pointerEvents: "none" }}
                              />
                            </div>
                          )}

                          {mainItem?.type === "dropdown" && (
                            <div style={{ position: "relative" }}>
                              <Select
                                showSearch
                                allowClear
                                filterOption={(
                                  input,
                                  option, // 👈 filter logic
                                ) =>
                                  (option?.label ?? "")
                                    .toLowerCase()
                                    .includes(input.toLowerCase())
                                }
                                suffixIcon={null}
                                value={mainItem.Answer}
                                style={{
                                  width: "100%",
                                  height: "40px",
                                }}
                                placeholder=""
                                onChange={(e, data) =>
                                  handleChangeInput({
                                    value: data?.label,
                                    i,
                                    key: mainItem?.key,
                                  })
                                }
                                options={dropdownsListObjAll?.[mainItem.key]}
                              />
                            </div>
                          )}
                        </ConditionalRenderer>
                        <ConditionalRenderer condition={!allowUpdate}>
                          <Input
                            value={mainItem?.Answer || ""}
                            className={styles.input}
                            style={{
                              pointerEvents: "none",
                            }}
                          />
                        </ConditionalRenderer>
                      </div>
                    </ConditionalRenderer>
                  </>
                );
              })}
            </div>
            <ConditionalRenderer condition={allowUpdate}>
              <div className={styles.confirmation}>
                <p>{extractedTrans.addConfirmation}</p>
                <div className={styles.buttons}>
                  <Button
                    onClick={handleCloseModal}
                    type="primary"
                    style={{ backgroundColor: "#CD2A3F" }}
                    size="large"
                  >
                    {translator("no")}
                  </Button>
                  <Button
                    onClick={() => handleUpdateQueryData(queries)}
                    type="primary"
                    style={{ backgroundColor: "#CD2A3F" }}
                    size="large"
                  >
                    {translator("yes")}
                  </Button>
                </div>
              </div>
            </ConditionalRenderer>
          </div>
        </div>
      </Spin>
    </Modal>
  );
};
